<template>
  <Table
    :columns="alertColumns"
    :data="alerts"
    :max-height="600"
    :loading="loading"
    border
    @on-selection-change="handleRowSelectionChange"
  >
    <template
      slot="name"
      slot-scope="{ row }"
    >
      {{ alertDefinitions[row.alertId] && alertDefinitions[row.alertId].name }}
      <Tooltip
        :content="alertDefinitions[row.alertId] && alertDefinitions[row.alertId].text"
        max-width="200"
      >
        <i class="fas fa-info-circle" />
      </Tooltip>
    </template>
    <template
      slot="action"
      slot-scope="{ row, index }"
    >
      <Select
        :value="row.type"
        @on-change="val => handleConditionChange(val, index)"
      >
        <Option
          v-for="conditionOption in createConditionOptionsForAlert(row.alertId)"
          :key="conditionOption.value"
          :value="conditionOption.value"
          :label="conditionOption.label"
        />
      </Select>
    </template>
    <template
      slot="threshold"
      slot-scope="{ row, index }"
    >
      <template v-if="row.type === 1">
        <InputNumber
          :value="formatThresholdValue(row)"
          size="small"
          @on-change="val => handleThresholdChange(val, index)"
        />
        {{ alertDefinitions[row.alertId].format === 'percentage' && '%' || '' }}
      </template>
      <template v-else-if="row.type === 2">
        <InputNumber
          :value="row.dayPercentage"
          :min="0"
          size="small"
          @on-change="val => handlePercentageChange('day', val, index)"
        />
        %
      </template>
      <template v-else-if="row.type === 3">
        <InputNumber
          :value="row.weekPercentage"
          :min="0"
          size="small"
          @on-change="val => handlePercentageChange('week', val, index)"
        />
        %
      </template>
    </template>
  </Table>
</template>

<script>
import _ from 'lodash'

export default {
  model: {
    prop: 'alerts',
    event: 'change'
  },

  props: {
    alerts: {
      type: Array,
      default: () => []
    },
    alertDefinitions: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      alertColumns: [{
        type: 'selection',
        width: 60,
        align: 'center'
      }, {
        title: 'Metric',
        width: 250,
        slot: 'name'
      }, {
        title: 'Condition',
        slot: 'action'
      }, {
        title: 'Threshold',
        width: 140,
        slot: 'threshold'
      }]
    }
  },

  methods: {
    createConditionOptionsForAlert (alertId) {
      return [
        {
          value: 1,
          label: this.alertDefinitions[alertId] && this.alertDefinitions[alertId].dayThreshold
        }, {
          value: 2,
          label: this.alertDefinitions[alertId] && this.alertDefinitions[alertId].dayPercentage
        }, {
          value: 3,
          label: this.alertDefinitions[alertId] && this.alertDefinitions[alertId].weekPercentage
        }
      ]
    },

    /**
     * Handler for when the condition has changed
     * @param {String} value
     * @param {Number} index
     */
    handleConditionChange (value, index) {
      const alerts = _.cloneDeep(this.alerts)

      alerts[index].type = value
      this.$emit('change', alerts)
    },

    /**
     * Handler for when the threshold has changed
     * @param {String} value
     * @param {Number} index
     */
    handleThresholdChange (value, index) {
      const alerts = _.cloneDeep(this.alerts)
      const alert = alerts[index]
      const { format } = this.alertDefinitions[alert.alertId]

      if (format === 'percentage') {
        alert.threshold = value / 100
      } else {
        alert.threshold = value
      }

      this.$emit('change', alerts)
    },

    /**
     * Handler for when the percentage change value has changed
     * @param {String} type - either 'day' or 'week'
     * @param {String} value
     * @param {Number} index
     */
    handlePercentageChange (type, value, index) {
      const alerts = _.cloneDeep(this.alerts)

      if (type === 'day') {
        alerts[index].dayPercentage = value
      } else if (type === 'week') {
        alerts[index].weekPercentage = value
      }

      this.$emit('change', alerts)
    },

    /**
     * Handler for when the row selection has changed
     * @param {Array} selections
     */
    handleRowSelectionChange (selections) {
      const alerts = _.cloneDeep(this.alerts)
      const alertMap = _.keyBy(alerts, 'alertId')

      alerts.forEach(alert => {
        alert.active = false
        alert._checked = false
      })

      selections.forEach(selection => {
        const selectedAlert = alertMap[selection.alertId]

        if (selectedAlert) {
          selectedAlert.active = true
          selectedAlert._checked = true
        }
      })

      this.$emit('change', alerts)
    },

    /**
     * Returns the formatted threshold value. Format depends on the metric.
     * @param {Object} alert
     * @return {Number}
     */
    formatThresholdValue (alert) {
      const { format } = this.alertDefinitions[alert.alertId]
      const { threshold } = alert

      if (format === 'percentage') {
        return threshold * 100
      } else {
        return threshold
      }
    }
  }
}
</script>
